import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions, user as authUser } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import {
  groupedIssueList,
  groupedIssueListExportUrl,
  SupportedExportFormat,
} from '@/services/api/resources/analytics';
import { handleIssueClick, redirectIssues } from '@/ui/pages/analytics/Overview/redirects';
import { useTask } from '@/ui/concerns';
import { download } from '@/ui/helpers/uiHelper';
import { useQueries } from '@/ui/pages/analytics/Overview/useQueries';
import { Status } from '@/entities/todo';
import { t } from '@/ui/i18n/i18n';
import IssueStatusTableCard from '@/_legacy/views/Analytics/Overview/IssueStatusTableCard.vue';
import { datesToDuration } from '@/ui/helpers/dateHelper';

export const IssueStatusCard = defineComponent({
  name: 'IssueStatusCard',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    const {
      issuesQuery,
    } = useQueries();

    const tableList = ref<unknown>({});

    const getList = useTask(
      () => {
        initialLoading.value = true;

        return groupedIssueList(datesToDuration(issuesQuery().start, issuesQuery().end), issuesQuery());
      },
      response => {
        tableList.value = {
          series: [
            {
              value: response.meta.openIssueCount,
              className: 'blue',
              label: t('status.pending'),
              meta: { status: Status.Pending },
            },
            {
              value: response.meta.inProgressIssueCount,
              className: 'orange',
              label: t('status.in-progress'),
              meta: { status: Status.InProgress },
            },
            {
              value: response.meta.resolvedIssueCount,
              className: 'green',
              label: t('status.done'),
              meta: { status: Status.Done },
            },
          ],
        };

        initialLoading.value = false;
      },
    );
    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => groupedIssueListExportUrl(
        datesToDuration(issuesQuery().start, issuesQuery().end),
        issuesQuery(),
        format as SupportedExportFormat,
        authUser.value.locale,
      ),
      download,
    );

    watch([
      () => issuesQuery(),
    ], getList.run, { deep: true });

    getList.run();

    return function render() {
      //legacy, not compatible with Vue typings
      //@ts-ignore
      return h(IssueStatusTableCard, {
        props: {
          chartData: tableList.value,
          loading: initialLoading.value,
        },
        on: {
          click: () => redirectIssues(issuesQuery()),
          exportClicked: (format: SupportedExportFormat) => exportData(format),
          rowClick: (meta: { status: Status }) => handleIssueClick(issuesQuery(), meta),
        },
      });
    };
  },
});
